import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { queueCartChange, visibleItemCount } from '@theme/cart-app-sync';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 150).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Seel fix: lines removed optimistically whose queued request has not finished yet.
   * @type {Set<LineRef>}
   */
  #pendingRemovals = new Set();

  /** Seel fix: stylesheet hiding the rows in `#pendingRemovals`. @type {HTMLStyleElement | null} */
  #pendingStyle = null;

  /**
   * Handles the line item removal.
   * @param {number | string} line - The 1-based line index, or the line item key.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.#rowFor(line);

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Optimistic empty state only when nothing else is queued; Seel re-renders the rows meanwhile.
    const isEmptyCart = this.#pendingRemovals.size <= 1 && rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      // This optimistic swap only replaces this component's children, so the
      // dialog's `cart-drawer--empty` (set server-side from `cart.empty?`) is
      // never touched by it; the drawer's later cart-update morph runs in
      // 'hydration' mode, which only patches `data-hydration-key` nodes and
      // skips the dialog too — so it has to be set here or it never happens,
      // leaving the populated-state header divider showing over empty content.
      this.closest('.cart-drawer__dialog')?.classList.add('cart-drawer--empty');

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number | string} config.line - The 1-based line index, or the line item key.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const { line, quantity } = config;
    const row = this.#rowFor(line);
    const ref = row ? lineRefOf(row) : null;

    if (quantity === 0 && ref) {
      this.#pendingRemovals.add(ref);
      this.#syncPendingStyle();
    }
    this.refs.cartTotal?.shimmer();

    queueCartChange((cart) => {
      const key = ref ? resolveKey(ref, cart) : line;

      // The line is already gone, so just show the cart as it is.
      if (!key) return this.#renderCurrentCart();

      // Seel can still change the line (new key) after it was resolved, so on a 400 look it up again once.
      const retryStaleKey = ref
        ? async () => {
            const fresh = await fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());
            const freshKey = resolveKey(ref, fresh);

            return freshKey ? this.#changeQuantity({ ...config, line: freshKey }, line) : this.#renderCurrentCart();
          }
        : undefined;

      return this.#changeQuantity({ ...config, line: key }, line, retryStaleKey);
    })
      .then(() => quantity === 0 && this.#reconcileRenderedCart())
      .catch((error) => console.error(error))
      .finally(() => {
        if (quantity !== 0 || !ref) return;
        this.#pendingRemovals.delete(ref);
        this.#syncPendingStyle();
      });
  }

  /** Seel fix: once Seel settles after a removal, re-renders if it painted a stale cart over ours. */
  #reconcileRenderedCart() {
    return queueCartChange(async (settled) => {
      const cart = settled ?? (await fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json()));
      const keys = new Set(cart.items.map((item) => item.key));
      const rendered = this.refs.cartItemRows ?? [];

      if (rendered.length === keys.size && rendered.every((row) => keys.has(row.dataset.key ?? ''))) return;

      // Dispatched from the document so every cart and the header count re-render.
      document.dispatchEvent(
        new CartUpdateEvent(cart, 'cart-items-component', { itemCount: visibleItemCount(cart), source: 'cart-items-component' })
      );
    });
  }

  /** Seel fix: re-renders the cart when the line to change is already gone. */
  async #renderCurrentCart() {
    await sectionRenderer.renderSection(this.sectionId, { cache: false });
    resetShimmer(this);
  }

  /**
   * Seel fix: finds the row by key, since Seel reorders lines and positions go stale.
   * @param {number | string} line - The 1-based line index, or the line item key.
   * @returns {HTMLTableRowElement | undefined}
   */
  #rowFor(line) {
    return typeof line === 'string'
      ? this.refs.cartItemRows.find((row) => row.dataset.key === line)
      : this.refs.cartItemRows[line - 1];
  }

  /**
   * Seel fix: hides queued removals via a stylesheet, so Seel's own drawer re-renders can't show them.
   */
  #syncPendingStyle() {
    const selectors = [...this.#pendingRemovals].flatMap(pendingRowSelectors);

    if (selectors.length === 0) {
      this.#pendingStyle?.remove();
      this.#pendingStyle = null;
      return;
    }

    this.#pendingStyle ??= document.head.appendChild(document.createElement('style'));
    this.#pendingStyle.textContent = `${selectors.join(',\n')} { display: none !important; }`;
  }

  /**
   * Seel fix: sends the quantity change; split out of `updateQuantity` so it can be queued behind Seel.
   * @param {Object} config - The config.
   * @param {number | string} config.line - The 1-based line index, or the line item key.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {number | string} [errorLine] - The line the error refs are rendered for.
   * @param {() => Promise<unknown>} [onStaleKey] - Runs instead of the error handling when the key no longer exists.
   */
  #changeQuantity(config, errorLine = config.line, onStaleKey) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      ...(typeof line === 'string' ? { id: line } : { line }),
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    let status = 0;

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        status = response.status;
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        if (status === 400 && onStaleKey) return onStaleKey();

        resetShimmer(this);

        // Shopify answers an over-stock change with a 422 `{ message, description }` (no
        // `errors`, no `sections`) after clamping the line to the available quantity.
        if (parsedResponseText.errors || status >= 400) {
          return this.#handleCartError(errorLine, parsedResponseText, quantity);
        }

        // A successful change can still come back with `sections: null`, so re-render every cart instead.
        if (!parsedResponseText.sections) {
          document.dispatchEvent(
            new CartUpdateEvent(parsedResponseText, 'cart-items-component', {
              itemCount: visibleItemCount(parsedResponseText),
              source: 'cart-items-component',
            })
          );
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error. When Shopify clamped the line to the available stock, re-renders
   * the cart so it shows the real quantity, then tells the shopper why.
   * @param {number | string} line - The 1-based line index, or the line item key.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} [parsedResponseText.errors] - The errors.
   * @param {string} [parsedResponseText.description] - The description of a 422 response.
   * @param {string} [parsedResponseText.message] - The message of a 422 response.
   * @param {number} requestedQuantity - The quantity the shopper asked for.
   */
  #handleCartError = async (line, parsedResponseText, requestedQuantity) => {
    let message = parsedResponseText.errors || parsedResponseText.description || parsedResponseText.message || '';

    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());
      const available = cart.items?.[line - 1]?.quantity;
      const template = Theme.translations.quantity_limited_to_stock;

      // The error can arrive as `errors` or as a 422 body, so key off the clamp itself.
      if (available && template && available < requestedQuantity) {
        message = template.replace('{{ quantity }}', String(available));
      }

      await sectionRenderer.renderSection(this.sectionId, { cache: false });
    } catch (error) {
      console.error(error);
    }

    // The re-render replaces the row's nodes, so look the refs up afresh.
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement) || !(cartItemErrorContainer instanceof HTMLElement)) return;

    cartItemError.textContent = message;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

/**
 * Seel fix: identifies a line after Seel changes its key (line properties) or swaps its own variant.
 * @typedef {{key: string, variantId: string, handle: string, isSeel: boolean}} LineRef
 */

/**
 * Seel fix: reads what identifies the row's line, for finding it again after Seel changes it.
 * @param {HTMLElement} row - A cart item row.
 * @returns {LineRef}
 */
function lineRefOf(row) {
  const { key = '', variantId = '', handle = '' } = row.dataset;
  return { key, variantId, handle, isSeel: row.hasAttribute('data-seel-line') };
}

/**
 * Seel fix: selectors for the line's rows, except the one still animating out.
 * @param {LineRef} ref
 * @returns {string[]}
 */
function pendingRowSelectors(ref) {
  /** @param {string} name @param {string} value */
  const attr = (name, value) => `[data-${name}="${CSS.escape(value)}"]`;
  const line = ref.isSeel
    ? `[data-seel-line]${attr('handle', ref.handle)}`
    : `:not([data-seel-line])${attr('variant-id', ref.variantId)}`;

  return [`tr${attr('key', ref.key)}`, `tr${attr('parent-key', ref.key)}`, `tr${line}`].map(
    (selector) => `${selector}:not(.removing)`
  );
}

/**
 * Seel fix: the line's key in the cart as it is now, after Seel may have changed it.
 * @param {LineRef} ref
 * @param {import('./cart-app-sync').Cart | null} cart - Null when nothing changed since page load.
 * @returns {string | undefined}
 */
function resolveKey(ref, cart) {
  if (!cart) return ref.key;

  const item =
    cart.items.find((item) => item.key === ref.key) ??
    cart.items.find((item) =>
      ref.isSeel ? item.handle === ref.handle : String(item.variant_id) === ref.variantId
    );

  return item?.key;
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
